import { Add, SettingsOutlined } from '@mui/icons-material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Button, Stack } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import Box from '@mui/material/Box';
import { DataGridProProps, GridColumnVisibilityModel, GridSortModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useLocation } from 'react-router';
import { StyleObj } from '../../@types';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import { LS_KEYS } from '../../constants';
import { useModal } from '../../contexts/ModalContext';
import { AdditionalQueryParams } from '../../hooks/usePagination';
import usePersist from '../../hooks/usePersist';
import useVisibleColumns from '../../hooks/useVisibleColumns';
import FilterModal, { FilterFeatureProps, FilterTypes } from '../modals/FilterModal';
import FormModal, { FormTypes, getFormModalOptions } from '../modals/FormModal';
import CustomizeTable from '../molecules/CustomizeTable';
import StyledDataGrid from '../organisms/StyledDataGrid';
import TableActionButton from '../atoms/TableActionButton';
import CustomToolbar from '../molecules/CustomToolbar';
import { useFeature } from '@growthbook/growthbook-react';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { StyledMobileDataGrid } from '../organisms/StyledMobileDataGrid/StyledMobileDataGrid';
import type { MobileCardHeaderItems } from '../organisms/StyledMobileDataGrid/types';
import SearchTableData from '../molecules/SearchTableData';

import { mergeSx } from '../../utils/styles';

type TableTemplateProps<R extends GridValidRowModel> = DataGridProProps<R> & {
  handleSearch?: (value: string | null) => void;
  createItemBtnLabel?: string;
  createItemBtnStyle?: ButtonProps['sx'];
  defaultVisibleColumns?: GridColumnVisibilityModel;
  visibilityStorageKey?: string;
  handleDelete?: (id: string) => void;
  handlePaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  handleSort?: (model: GridSortModel) => void;
  changeQuery?: (query: AdditionalQueryParams) => void;
  formType?: FormTypes;
  formProps?: Record<string, unknown>;
  customTableHeaderContent?: JSX.Element;
  customTableFooter?: JSX.Element;
  showFilter?: boolean;
  showCustomizeTable?: boolean;
  showExport?: boolean;
  exportAllColumns?: boolean;
  disableFilter?: boolean;
  filterType?: FilterTypes;
  filterProps?: FilterFeatureProps;
  mobileCardHeaderItems?: MobileCardHeaderItems<R>;
};

const makeStyles = (createItemBtnStyle: ButtonProps['sx'] = {}, showSearch: boolean): StyleObj => ({
  container: (theme) => ({
    minHeight: 0,
    minWidth: 0,
    flex: 1,
    height: '100%',
    [theme.breakpoints.down('md')]: {
      minHeight: '700px',
    },
  }),
  topContainer: (theme) => ({
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    mb: 4,
    gap: 2,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column-reverse',
      rowGap: 1,
      mb: 0,
    },
  }),
  topContentWrapper: (theme) => ({
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    width: '385px',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    pl: showSearch ? 0 : 1,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
      px: 2,
      gap: 0,
      mb: 1,
    },
  }),
  customTableHeaderContent: (theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    columnGap: 2,
    flexWrap: 'wrap',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      mb: 0,
      px: 2,
    },
  }),
  actionBtnContainer: (theme) => ({
    ml: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      px: 2,
    },
  }),
  actionBtn: (theme) => ({
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      borderRadius: 1,
    },
    ...createItemBtnStyle,
  }),
});

const TableTemplate = <R extends GridValidRowModel>(props: TableTemplateProps<R>) => {
  const {
    rows = [],
    rowCount = 0,
    columns,
    handleSort,
    handlePaginationModelChange,
    handleSearch,
    createItemBtnLabel,
    createItemBtnStyle,
    defaultVisibleColumns,
    visibilityStorageKey,
    handleDelete,
    changeQuery = () => void 0,
    formType,
    formProps,
    customTableHeaderContent,
    customTableFooter,
    showFilter = true,
    showExport = false,
    exportAllColumns = false,
    disableFilter = false,
    filterType,
    filterProps,
    mobileCardHeaderItems,
    columnGroupingModel,
    paginationModel: controlledPaginationModel,
    ...rest
  } = props;
  const [paginationModel, setPaginationModel] = useState({
    page: controlledPaginationModel?.page ?? 0,
    pageSize: controlledPaginationModel?.pageSize ?? 25,
  });
  const { isMobile } = useBreakpoints();
  const { on: isMobileDataGridEnabled } = useFeature(FEATURE_FLAG.MOBILE_DATA_GRID);
  const showSearchBar = !!handleSearch;
  const styles = makeStyles(createItemBtnStyle, showSearchBar);

  // Sorting defaults to server mode, so a column left sortable on a table that never passes
  // `handleSort` renders an arrow that silently does nothing. Strip sortability in that case,
  // unless the table opted into client-side sorting (where MUI sorts the rows it already has).
  const sortableColumns = useMemo(
    () =>
      handleSort || rest.sortingMode === 'client' ? columns : columns.map((column) => ({ ...column, sortable: false })),
    [columns, handleSort, rest.sortingMode]
  );

  const { visibleColumns, setVisibleColumns, visibleColumnsLSKey } = useVisibleColumns({
    manualLSKey: visibilityStorageKey,
    columns,
    defaultVisibleColumns: defaultVisibleColumns || {},
  });
  const { openModal } = useModal();
  const { pathname } = useLocation();
  const { getPersistData, deletePersistData } = usePersist(false);

  const visibleColumnsLS = getPersistData<GridColumnVisibilityModel>(visibleColumnsLSKey);

  useEffect(() => {
    if (!controlledPaginationModel) return;

    setPaginationModel(controlledPaginationModel);
  }, [controlledPaginationModel]);

  useEffect(() => {
    // remove LS filter data on page change
    const formDataKey = `${LS_KEYS.appFormFilter}-${pathname}`;
    deletePersistData(formDataKey);
  }, [deletePersistData, pathname]);

  const handleCreate = () => {
    formType && openModal(<FormModal form={formType} {...formProps} />, undefined, getFormModalOptions(formType));
  };

  const resetPaginationPage = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onSearch = (value: string | null) => {
    if (handleSearch) {
      handleSearch(value?.trim() || null);
      resetPaginationPage();
    }
  };

  const onSortModelChange = (model: GridSortModel) => {
    if (!handleSort) return;

    resetPaginationPage();
    handleSort(model);
  };

  const onPaginationModelChange = (model: { page: number; pageSize: number }) => {
    if (!handlePaginationModelChange) return;
    setPaginationModel(model);
    handlePaginationModelChange(model);
  };

  const openCustomizationDialog = () => {
    if (!defaultVisibleColumns) return;

    openModal(
      <CustomizeTable
        columns={columns}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        visibleColumnsLSKey={visibleColumnsLSKey}
        columnGroupingModel={columnGroupingModel}
      />
    );
  };

  const openFilterDialog = () => {
    if (filterType) {
      openModal(<FilterModal filter={filterType} filterProps={filterProps} changeQuery={changeQuery} />);
      return;
    }

    const page = pathname.split('/').pop();

    // Still not ideal, we have to take care of order of filters inside the map
    // eg. place 'events-results' before 'events' to avoid matching events filter on results page with .includes
    const filterMap: Record<string, FilterTypes> = {
      'betting-reports/cashout': 'cashout-report',
      'betting-reports/jackpot': 'jackpot-report',
      'ticket-monitoring': 'ticket-monitoring',
      'ticket-authorization': 'ticket-authorization',
      'betting-reports/partners/agents': 'agents-report',
      'betting-reports/partners/affiliates': 'affiliates-report',
      'betting-reports/partners/managers': 'managers-report',
      'wallet-transaction': 'wallet-transaction',
      'liability-monitoring': 'liability-monitoring',
      'payment-transaction': 'payment-transaction',
      'total-credit': 'total-credit',
      'crm/players': 'crm',
      'betting-reports/players': 'player-report',
      'betting-reports/sportsbook': 'sportsbook-report',
      'payments/total': 'payments-total',
      'payments/list': 'payments-list',
      'betting-reports/daily': 'daily',
      'publish-events': 'publish-events',
      'betting-reports/bonus': 'bonus-reports',
      jackpot: 'jackpot',
      events: 'events',
      outrights: 'outrights',
      partners: 'partners',
      'bonus/welcome': 'welcome-bonus',
      'bonus/cashback': 'cashback-bonus',
      'bonus/select': 'num-of-selections-bonus',
      results: 'results',
      marketplace: 'marketplace',
      campaigns: 'campaigns',
      'affiliate/campaign-reports': 'campaign-reports',
      'payments/approvals': 'approvals',
      'casino/games': 'casino-games',
      'casino/transactions': 'casino-transactions',
      'casino/bets': 'casino-bets',
      'casino/reports': 'casino-reports',
    };

    let filter = '*';

    for (const [pattern, value] of Object.entries(filterMap)) {
      if (page === value) {
        filter = value;
        break;
      }
      if (pathname.includes(pattern)) {
        filter = value;
        break;
      }
    }

    openModal(<FilterModal filter={filter as FilterTypes} changeQuery={changeQuery} />);
  };

  const visibleColumnsHandler = !isEmpty(visibleColumns) ? visibleColumns : (visibleColumnsLS ?? defaultVisibleColumns);

  return (
    <Stack sx={styles.container}>
      <Stack
        sx={mergeSx(
          {
            width: 1,
          },
          styles.topContainer
        )}
      >
        <Box sx={styles.topContentWrapper}>
          {showSearchBar && <SearchTableData onSearch={onSearch} />}
          <Stack direction='row'>
            {defaultVisibleColumns && (
              <TableActionButton onClick={openCustomizationDialog} Icon={SettingsOutlined} title='Customize Table' />
            )}
            {showFilter && (
              <TableActionButton
                onClick={openFilterDialog}
                Icon={FilterAltOutlinedIcon}
                title='Filter'
                disabled={disableFilter}
              />
            )}
          </Stack>
          <Stack
            direction='row'
            sx={{
              justifyContent: 'flex-end',
            }}
          ></Stack>
        </Box>
        {customTableHeaderContent && <Box sx={styles.customTableHeaderContent}>{customTableHeaderContent}</Box>}
        {formType && createItemBtnLabel && (
          <Box sx={styles.actionBtnContainer}>
            <Button variant='outlined' startIcon={<Add />} onClick={handleCreate} sx={styles.actionBtn}>
              {createItemBtnLabel}
            </Button>
          </Box>
        )}
      </Stack>
      {isMobile && isMobileDataGridEnabled ? (
        <StyledMobileDataGrid
          rows={rows}
          columns={columns}
          columnVisibilityModel={visibleColumnsHandler}
          loading={rest.loading}
          page={paginationModel.page + 1}
          pageSize={paginationModel.pageSize}
          rowCount={rowCount}
          onPageChange={(newPage) => {
            const newModel = { ...paginationModel, page: newPage - 1 };
            setPaginationModel(newModel);
            handlePaginationModelChange?.(newModel);
          }}
          onPageSizeChange={(newSize) => {
            const newModel = { page: 0, pageSize: newSize };
            setPaginationModel(newModel);
            handlePaginationModelChange?.(newModel);
          }}
          mobileCardHeaderItems={mobileCardHeaderItems}
          {...rest}
        />
      ) : (
        <StyledDataGrid
          rows={rows}
          columns={sortableColumns}
          paginationModel={paginationModel}
          columnVisibilityModel={visibleColumnsHandler}
          onPaginationModelChange={onPaginationModelChange}
          onSortModelChange={onSortModelChange}
          rowCount={rowCount}
          columnGroupingModel={columnGroupingModel}
          slots={{
            toolbar: showExport
              ? () => CustomToolbar(showSearchBar, !!defaultVisibleColumns, exportAllColumns)
              : undefined,
          }}
          disableVirtualization
          {...rest}
        />
      )}
      {customTableFooter}
    </Stack>
  );
};

export default TableTemplate;
